/**
 * Clinic Location Massager Detail Modal
 *
 * A2: はり・きゅう師詳細 / R2: マッサージ師詳細 - 拠点管理 (Modal)
 */

import React, { useMemo } from 'react';
import { ClinicLocationMassager } from '@/pages/main/clinicLocation/types';
import { AppModal } from '@/components/atoms';
import { InfoRow, ScheduleTable } from '@/components/molecules';
import { formatPeriodDisplay } from '@/utils/dateConverter';
import { renderTableCellTruncate } from '@/utils/tableCellTruncate';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';

interface ClinicLocationMassagerDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  massagerData: ClinicLocationMassager | null;
  /** A2 or R2 - from page context (e.g. useTheme). Drives title/label (はり・きゅう師 / マッサージ師). */
  serviceType: string;
}

const ClinicLocationMassagerDetailModal: React.FC<ClinicLocationMassagerDetailModalProps> = ({
  isOpen,
  onClose,
  massagerData,
  serviceType,
}) => {
  const massager = massagerData;
  const isA2 = String(serviceType).toUpperCase() === SERVICE_TYPE_A2;
  const personTypeLabel = isA2 ? 'はり・きゅう師' : 'マッサージ師';

  // Prepare schedule data for ScheduleTable
  const scheduleData = useMemo(() => {
    if (!massager) return null;
    return {
      am: {
        monday: !!massager.is_am_monday,
        tuesday: !!massager.is_am_tuesday,
        wednesday: !!massager.is_am_wednesday,
        thursday: !!massager.is_am_thursday,
        friday: !!massager.is_am_friday,
        saturday: !!massager.is_am_saturday,
        sunday: !!massager.is_am_sunday,
      },
      pm: {
        monday: !!massager.is_pm_monday,
        tuesday: !!massager.is_pm_tuesday,
        wednesday: !!massager.is_pm_wednesday,
        thursday: !!massager.is_pm_thursday,
        friday: !!massager.is_pm_friday,
        saturday: !!massager.is_pm_saturday,
        sunday: !!massager.is_pm_sunday,
      },
    };
  }, [massager]);

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${personTypeLabel}詳細`}
      maxWidth="max-w-4xl"
      footer={null}
    >
      <div className="space-y-1">
        <InfoRow
          label={personTypeLabel}
          value={
            massager?.massager
              ? renderTableCellTruncate(
                  `${massager.massager.serial_number}:${massager.massager.name}`,
                  { lineClamp: 2, width: 560 }
                )
              : undefined
          }
        />
        <InfoRow
          label="有効期限"
          value={formatPeriodDisplay(massager?.available_from, massager?.available_to) || undefined}
        />

        {/* 勤務時間帯テーブル */}
        <div className="pt-6 mt-6">
          {scheduleData && (
            <ScheduleTable
              amTimeStart={massager?.am_time_start}
              amTimeEnd={massager?.am_time_end}
              pmTimeStart={massager?.pm_time_start}
              pmTimeEnd={massager?.pm_time_end}
              schedule={scheduleData}
            />
          )}
        </div>
      </div>
    </AppModal>
  );
};

export default ClinicLocationMassagerDetailModal;
